import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, message, Spin } from "antd";
import * as XLSX from "xlsx";

import { getAuthMethod } from "../../../services/HttpServices";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MailOutlined,
  RightOutlined,
  SaveOutlined,
  TeamOutlined,
  WarningOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { audienceSourceOptions, audienceSteps } from "./audienceSourceOptions";
import {
  initialManualRows,
  audienceDetailRows,
} from "./audienceFlowData";
import AudienceFlowHeader from "./AudienceFlowHeader";
import {
  AudienceManualPrepareStep,
  AudienceMappingStep,
  AudienceReviewStep,
  AudienceSourceStep,
  AudienceUploadStep,
  AudienceValidationStep,
} from "./AudienceFlowSteps";
import {
  AudienceDetailScreen,
  AudienceSuccessScreen,
} from "./AudienceFlowOutcomeScreens";
import Loader from "../../../components/loader";
import {
  createAudience,
  updateAudience,
  manualImport,
  uploadCsvForPreview,
  confirmImportJob,
  importGoogleContacts,
  importMicrosoftContacts,
  getAudienceById
} from "../../../services/audience.service";
import { useDispatch, useSelector } from "react-redux";
import { getSummary, connectToSocial, disconnectImport } from "../../../actionCreator/importActionCreator";

const BookmarkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const CreateAudienceFlow = ({ onBack, onFinish, existingAudienceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const summary = useSelector((state) => state.importReducer.summary);
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [screen, setScreen] = useState("flow");
  const [selectedSource, setSelectedSource] = useState("manual");
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedHeaders, setParsedHeaders] = useState([]);
  const [parsedPreviewRows, setParsedPreviewRows] = useState([]);
  const [totalParsedRows, setTotalParsedRows] = useState(0);
  const [columnMapping, setColumnMapping] = useState({});
  const [audienceName, setAudienceName] = useState("");
  const [audienceDescription, setAudienceDescription] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [audienceTags, setAudienceTags] = useState([]);
  const [manualRows, setManualRows] = useState(initialManualRows);
  const [visibility, setVisibility] = useState("public");

  const [isProcessing, setIsProcessing] = useState(false);
  const [audienceId, setAudienceId] = useState(existingAudienceId || null);
  const [jobId, setJobId] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    dispatch(getSummary());
  }, [dispatch]);

  useEffect(() => {
    // Fetch available tags
    getAuthMethod("api/tags?type.equals=AUDIENCE")
      .then((res) => {
        if (res.data && Array.isArray(res.data.tagsDTOs)) {
          setAvailableTags(res.data.tagsDTOs.map(t => ({ label: t.name, value: t.name })));
        } else if (Array.isArray(res.data)) {
          setAvailableTags(res.data.map(t => ({ label: t.name, value: t.name })));
        }
      })
      .catch(err => console.error("Failed to load audience tags", err));
  }, []);

  useEffect(() => {
    if (existingAudienceId) {
      getAudienceById(existingAudienceId)
        .then((res) => {
          if (res && res.data) {
            setAudienceName(res.data.name || "");
            setAudienceDescription(res.data.description || "");
            setAudienceTags(res.data.tags || []);
            setVisibility(res.data.visibility ? res.data.visibility.toLowerCase() : "public");
          }
        })
        .catch((err) => {
          console.error("Failed to fetch existing audience", err);
        });
    }
  }, [existingAudienceId]);

  const isGmailConnected = useMemo(() => {
    return summary?.sources?.find(d => d?.name === "Gmail Import")?.status === "Connected";
  }, [summary]);

  const isOutlookConnected = useMemo(() => {
    return summary?.sources?.find(d => d?.name === "Outlook Import")?.status === "Connected";
  }, [summary]);

  const isManualSource = selectedSource === "manual";
  const maxSteps = isManualSource 
    ? (existingAudienceId ? 3 : 4) 
    : (existingAudienceId ? 4 : 5);

  const visualStep = useMemo(() => {
    if (currentStep === 1) return 1;
    if (isManualSource) {
      if (currentStep === 2) return 2;
      return 3;
    } else {
      if (currentStep === 2 || currentStep === 3) return 2;
      return 3;
    }
  }, [currentStep, isManualSource]);

  const computedSteps = useMemo(
    () =>
      audienceSteps.map((step) => ({
        ...step,
        state:
          step.number < visualStep
            ? "done"
            : step.number === visualStep
              ? "current"
              : "upcoming",
      })),
    [visualStep],
  );

  const primaryButtonLabel = useMemo(() => {
    if (currentStep === 1) {
      if (selectedSource === "google" && !isGmailConnected) return "Connect Account";
      if (selectedSource === "outlook" && !isOutlookConnected) return "Connect Account";
      if (selectedSource === "google" || selectedSource === "outlook") return "Import Contacts";
      return "Continue";
    }
    
    if (isManualSource) {
      if (currentStep === 2) return "Validate Data";
      if (existingAudienceId) {
        if (currentStep === 3) return "Import Members";
      } else {
        if (currentStep === 3) return "Name your audience";
        if (currentStep === 4) return "Save Audience";
      }
    } else {
      if (currentStep === 2) return selectedFile ? "Map Columns" : "Continue";
      if (currentStep === 3) return "Validate Data";
      if (existingAudienceId) {
        if (currentStep === 4) return "Import Members";
      } else {
        if (currentStep === 4) return "Name your audience";
        if (currentStep === 5) return "Save Audience";
      }
    }
    return "Continue";
  }, [currentStep, isManualSource, selectedFile, existingAudienceId, selectedSource, isGmailConnected, isOutlookConnected]);

  const handleDisconnect = async (sourceType) => {
    try {
      setIsProcessing(true);
      const authType = sourceType === "google" ? "Gmail" : "Outlook";
      const res = await dispatch(disconnectImport(authType));
      if (res?.status === 200) {
        message.success(`${authType} account disconnected successfully.`);
        dispatch(getSummary());
      } else {
        message.error(`Failed to disconnect ${authType} account. Please try again.`);
      }
    } catch (err) {
      console.error("Failed to disconnect", err);
      message.error("Failed to disconnect account.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConnect = async (sourceType) => {
    try {
      setIsProcessing(true);
      const authType = sourceType === "google" ? "Gmail" : "Outlook";
      localStorage.setItem('oauth_return_to', window.location.pathname);
      const res = await dispatch(connectToSocial(authType));
      if (res?.status === 200) {
        window.open(res?.data?.url, "_self");
      } else {
        message.error("Failed to connect to " + authType);
      }
    } catch (err) {
      console.error("Failed to connect", err);
      message.error("Failed to connect account.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = async () => {
    try {
      if (currentStep === maxSteps) {
        setIsProcessing(true);
        if (!existingAudienceId) {
          const existingAudience = await getAudienceById(audienceId);
          await updateAudience(audienceId, {
            ...existingAudience.data,
            name: audienceName,
            tags: audienceTags,
            visibility: visibility ? visibility.toUpperCase() : "PUBLIC",
            description: audienceDescription
          });
        }
        await confirmImportJob(jobId, allowDuplicates);
        
        setIsProcessing(false);
        if (existingAudienceId) {
          onFinish?.();
        } else {
          setScreen("success");
        }
        return;
      }

      if (currentStep === 1) {
        if (selectedSource === "google" || selectedSource === "outlook") {
          const isConnected = selectedSource === "google" ? isGmailConnected : isOutlookConnected;
          const authType = selectedSource === "google" ? "Gmail" : "Outlook";
          
          if (!isConnected) {
            localStorage.setItem('oauth_return_to', window.location.pathname);
            const res = await dispatch(connectToSocial(authType));
            if (res?.status === 200) {
              window.open(res?.data?.url, "_self");
            } else {
              message.error("Failed to connect to " + authType);
            }
            return;
          }

          setIsProcessing(true);
          let newAudienceId = audienceId;
          if (!existingAudienceId) {
            const audienceRes = await createAudience({
              name: "Draft Audience - " + new Date().toLocaleDateString(),
              tags: [],
              visibility: "PUBLIC",
              description: ""
            });
            newAudienceId = audienceRes.data.id;
            setAudienceId(newAudienceId);
          }

          // Trigger OAuth import
          const jobRes = selectedSource === "google" 
            ? await importGoogleContacts(newAudienceId)
            : await importMicrosoftContacts(newAudienceId);
          
          setJobId(jobRes.data.id);
          setApiStats({
            total: jobRes.data.validCount + jobRes.data.duplicateCount + jobRes.data.invalidCount,
            whatsapp: jobRes.data.whatsappReadyCount || 0,
            email: jobRes.data.emailReadyCount || 0,
            invalid: jobRes.data.invalidCount,
            invalidEmail: jobRes.data.invalidEmailCount || 0,
            invalidPhone: jobRes.data.invalidPhoneCount || 0,
            duplicates: jobRes.data.duplicateCount || 0
          });
          
          setCurrentStep(4); // Skip straight to validation
          setIsProcessing(false);
        } else {
          setCurrentStep(2);
        }
      } 
      else if (currentStep === 2) {
        if (!isManualSource && selectedSource === "csv") {
          if (!selectedFile) {
            message.error("Please upload a CSV or Excel file to proceed.");
            return;
          }
          setCurrentStep(3);
        } else if (isManualSource) {
          const activeRows = manualRows.filter(r => r.name?.trim() || r.phone?.trim() || r.email?.trim());
          if (activeRows.length === 0) {
            message.error("Please add at least one contact to proceed.");
            return;
          }

          setIsProcessing(true);
          let newAudienceId = audienceId;
          if (!existingAudienceId && !newAudienceId) {
             const audienceRes = await createAudience({
               name: "Draft Audience - " + new Date().toLocaleDateString(),
               tags: [],
               visibility: "PUBLIC",
               description: ""
             });
             newAudienceId = audienceRes.data.id;
             setAudienceId(newAudienceId);
          }

          const members = activeRows.map(r => ({
            name: r.name,
            phoneNumber: r.phone,
            email: r.email,
            isWhatsappOptedIn: true
          }));

          const jobRes = await manualImport(newAudienceId, members);
          setJobId(jobRes.data.id);
          setApiStats({
            total: jobRes.data.validCount + jobRes.data.duplicateCount + jobRes.data.invalidCount,
            whatsapp: jobRes.data.whatsappReadyCount || 0,
            email: jobRes.data.emailReadyCount || 0,
            invalid: jobRes.data.invalidCount,
            invalidEmail: jobRes.data.invalidEmailCount || 0,
            invalidPhone: jobRes.data.invalidPhoneCount || 0,
            duplicates: jobRes.data.duplicateCount || 0
          });

          setCurrentStep((step) => step + 1);
          setIsProcessing(false);
        } else {
          setCurrentStep((step) => step + 1);
        }
      }
      else if (currentStep === 3) {
        if (!isManualSource && selectedSource === "csv") {
          const hasPhoneMapping = Object.values(columnMapping).includes("phone");
          if (!hasPhoneMapping) {
            message.error("Please map at least one column to 'Phone' to import contacts.");
            return;
          }

          setIsProcessing(true);
          let newAudienceId = audienceId;
          if (!existingAudienceId) {
            const audienceRes = await createAudience({
              name: "Draft Audience - " + new Date().toLocaleDateString(),
              tags: [],
              visibility: "PUBLIC",
              description: ""
            });
            newAudienceId = audienceRes.data.id;
            setAudienceId(newAudienceId);
          }

          const jobRes = await uploadCsvForPreview(newAudienceId, selectedFile, columnMapping);
          setJobId(jobRes.data.id);
          setApiStats({
            total: jobRes.data.validCount + jobRes.data.duplicateCount + jobRes.data.invalidCount,
            whatsapp: jobRes.data.whatsappReadyCount || 0,
            email: jobRes.data.emailReadyCount || 0,
            invalid: jobRes.data.invalidCount,
            invalidEmail: jobRes.data.invalidEmailCount || 0,
            invalidPhone: jobRes.data.invalidPhoneCount || 0,
            duplicates: jobRes.data.duplicateCount || 0
          });
          setCurrentStep(4);
          setIsProcessing(false);
        } else {
          setCurrentStep((step) => step + 1);
        }
      }
      else {
        setCurrentStep((step) => step + 1);
      }
    } catch (error) {
      console.error("Error in flow:", error);
      message.error(error?.response?.data?.message || error.message || "An error occurred during import");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    if (screen === "detail") {
      setScreen("success");
      return;
    }

    if (currentStep === 1) {
      if (onBack) {
        onBack();
      } else {
        navigate(-1);
      }
      return;
    }
    
    if (currentStep === 4 && (selectedSource === "google" || selectedSource === "outlook")) {
      setCurrentStep(1);
      return;
    }

    setCurrentStep((step) => Math.max(1, step - 1));
  };

  const handleFileChange = (event) => {
    const [file] = event.target.files || [];
    if (file) {
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        
        if (rows.length > 0) {
          const headers = rows[0].map(h => h ? h.toString().trim() : "");
          setParsedHeaders(headers);
          
          const preview = rows.slice(1, 11).filter(r => r && r.length > 0).map((row, index) => {
            const rowData = { id: index + 1, srNo: (index + 1).toString() };
            headers.forEach((h, i) => {
              rowData[h] = row[i] !== undefined ? row[i] : "";
            });
            return rowData;
          });
          setParsedPreviewRows(preview);
          setTotalParsedRows(Math.max(0, rows.length - 1));
          
          // Auto map common fields
          const initialMapping = {};
          headers.forEach(h => {
            const norm = h.toLowerCase().replace(/[^a-z0-9]/g, "");
            if (norm.includes("phone") || norm.includes("mobile") || norm.includes("whatsapp")) {
              initialMapping[h] = "phone";
            } else if (norm.includes("email")) {
              initialMapping[h] = "email";
            } else if (norm.includes("name") && !norm.includes("company")) {
              initialMapping[h] = "name";
            } else if (norm.includes("company") || norm.includes("organization")) {
              initialMapping[h] = "company";
            } else {
              initialMapping[h] = "ignore";
            }
          });
          setColumnMapping(initialMapping);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (!trimmedTag || audienceTags.includes(trimmedTag)) {
      setTagInput("");
      return;
    }

    setAudienceTags((prev) => [...prev, trimmedTag]);
    setTagInput("");
  };

  const handleManualRowChange = (id, field, value) => {
    setManualRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const handleAddManualRow = () => {
    setManualRows((prev) => [
      ...prev,
      { id: Date.now(), name: "", phone: "", email: "" },
    ]);
  };

  const handleDeleteManualRow = (id) => {
    setManualRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleColumnMappingChange = (field, value) => {
    setColumnMapping((prev) => ({ ...prev, [field]: value }));
  };

  const stats = useMemo(() => {
    return apiStats || {
      total: 0,
      whatsapp: 0,
      email: 0,
      invalid: 0,
      duplicates: 0,
      invalidEmail: 0,
      invalidPhone: 0,
    };
  }, [apiStats]);

  const displayRows = useMemo(() => {
    if (!isManualSource) {
      return audienceDetailRows;
    }

    return manualRows
      .filter((row) => row.name?.trim() || row.phone?.trim() || row.email?.trim())
      .map((row) => {
        const channels = [];
        if (row.phone?.trim()) channels.push("whatsapp");
        if (row.email?.trim()) channels.push("email");
        return {
          id: row.id,
          name: row.name?.trim() || "Unnamed Contact",
          phone: row.phone?.trim() || "-",
          email: row.email?.trim() || "-",
          channels,
        };
      });
  }, [isManualSource, manualRows]);

  const computedValidationCards = useMemo(() => {
    const totalCount = stats.total;
    const whatsappCount = stats.whatsapp;
    const emailCount = stats.email;
    const invalidCount = stats.invalid;

    const getPercentWidth = (val) => {
      if (totalCount === 0) return "0%";
      const pct = Math.min(100, Math.round((val / totalCount) * 100));
      return `${pct}%`;
    };

    return [
      {
        label: "Total Contacts",
        value: String(totalCount),
        progressWidth: getPercentWidth(totalCount),
        iconWrapClass: "tw-bg-secondary-color tw-text-theme",
        progressClass: "tw-bg-theme",
        icon: <TeamOutlined />,
      },
      {
        label: "Whatsapp ready",
        value: String(whatsappCount),
        progressWidth: getPercentWidth(whatsappCount),
        iconWrapClass: "tw-bg-[#EAFBF0] tw-text-[#22C55E]",
        progressClass: "tw-bg-[#22C55E]",
        icon: <WhatsAppOutlined />,
      },
      {
        label: "Email ready",
        value: String(emailCount),
        progressWidth: getPercentWidth(emailCount),
        iconWrapClass: "tw-bg-secondary-color tw-text-theme",
        progressClass: "tw-bg-theme",
        icon: <MailOutlined />,
      },
      {
        label: "Invalid",
        value: String(invalidCount),
        progressWidth: getPercentWidth(invalidCount),
        iconWrapClass: "tw-bg-[#FFF1ED] tw-text-[#FF7A59]",
        progressClass: "tw-bg-[#FF7A59]",
        icon: <WarningOutlined />,
        details: `${stats.invalidEmail || 0} Emails, ${stats.invalidPhone || 0} Phones`
      },
    ];
  }, [stats]);

  const renderCurrentStep = () => {
    if (currentStep === 1) {
      return (
        <AudienceSourceStep
          options={audienceSourceOptions}
          selectedSource={selectedSource}
          onSelect={setSelectedSource}
          isGmailConnected={isGmailConnected}
          isOutlookConnected={isOutlookConnected}
          onDisconnect={handleDisconnect}
          onConnect={handleConnect}
        />
      );
    }

    if (currentStep === 2) {
      return isManualSource ? (
        <AudienceManualPrepareStep
          manualRows={manualRows}
          onAddManualRow={handleAddManualRow}
          onDeleteManualRow={handleDeleteManualRow}
          onManualRowChange={handleManualRowChange}
          audienceName={audienceName}
          onAudienceNameChange={setAudienceName}
        />
      ) : (
        <AudienceUploadStep
          fileInputRef={fileInputRef}
          selectedFile={selectedFile}
          onFileChange={handleFileChange}
          totalParsedRows={totalParsedRows}
        />
      );
    }

    if (isManualSource) {
      if (currentStep === 3) {
        return <AudienceValidationStep 
          validationCards={computedValidationCards} 
          jobId={jobId} 
          stats={stats}
          allowDuplicates={allowDuplicates}
          onAllowDuplicatesChange={setAllowDuplicates}
        />;
      }
      return (
        <AudienceReviewStep
          audienceName={audienceName}
          audienceDescription={audienceDescription}
          audienceTags={audienceTags}
          onAudienceNameChange={setAudienceName}
          onAudienceDescriptionChange={setAudienceDescription}
          onAudienceTagsChange={setAudienceTags}
          availableTags={availableTags}
          visibility={visibility}
          onVisibilityChange={setVisibility}
          isManualSource={true}
          displayRows={displayRows}
          stats={stats}
        />
      );
    } else {
      if (currentStep === 3) {
        return (
          <AudienceMappingStep
            columnMapping={columnMapping}
            onColumnMappingChange={handleColumnMappingChange}
            parsedHeaders={parsedHeaders}
            parsedPreviewRows={parsedPreviewRows}
            totalParsedRows={totalParsedRows}
          />
        );
      }
      if (currentStep === 4) {
        return <AudienceValidationStep 
          validationCards={computedValidationCards} 
          jobId={jobId} 
          stats={stats}
          allowDuplicates={allowDuplicates}
          onAllowDuplicatesChange={setAllowDuplicates}
        />;
      }
      return (
        <AudienceReviewStep
          audienceName={audienceName}
          audienceDescription={audienceDescription}
          audienceTags={audienceTags}
          onAudienceNameChange={setAudienceName}
          onAudienceDescriptionChange={setAudienceDescription}
          onAudienceTagsChange={setAudienceTags}
          availableTags={availableTags}
          visibility={visibility}
          onVisibilityChange={setVisibility}
          isManualSource={false}
          displayRows={displayRows}
          stats={stats}
        />
      );
    }
  };

  if (screen === "success") {
    return (
      <div className="tw-h-[calc(100vh-112px)] tw-bg-white tw-rounded-2xl tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)] tw-border tw-border-solid tw-border-slate-100 tw-overflow-y-auto">
        <AudienceSuccessScreen
          audienceName={audienceName}
          totalContacts={stats.total}
          selectedSource={selectedSource}
          onFinish={() => onFinish?.()}
          onViewDetail={() => setScreen("detail")}
        />
      </div>
    );
  }

  if (screen === "detail") {
    return (
      <div className="tw-h-[calc(100vh-112px)] tw-bg-white tw-rounded-2xl tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)] tw-border tw-border-solid tw-border-slate-100 tw-overflow-y-auto">
        <AudienceDetailScreen
          stats={stats}
          displayRows={displayRows}
          onBackToListing={() => onFinish?.()}
        />
      </div>
    );
  }

  const containerClass = existingAudienceId 
    ? "tw-h-full tw-w-full tw-flex tw-flex-col tw-items-center tw-overflow-hidden tw-bg-white tw-px-6 tw-py-4" 
    : "tw-w-full tw-flex tw-flex-col tw-items-center tw-bg-white tw-px-6 tw-py-6 tw-pb-10";

  const wrapperClass = existingAudienceId
    ? "tw-mx-auto tw-max-w-[985px] tw-w-full tw-flex tw-flex-col tw-flex-grow tw-overflow-hidden"
    : "tw-mx-auto tw-max-w-[985px] tw-w-full tw-flex tw-flex-col";

  const innerClass = existingAudienceId
    ? "tw-flex-grow tw-overflow-y-auto tw-my-2 tw-pr-1"
    : "tw-my-4";

  if (isProcessing) {
    return (
      <div className="tw-min-h-[65vh] tw-w-full tw-flex tw-items-center tw-justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={wrapperClass}>
        <div className="tw-flex-shrink-0">
          <AudienceFlowHeader 
            steps={computedSteps} 
            title={existingAudienceId ? "Add Members" : "Create Audience"}
            subtitle={existingAudienceId ? `Add new contacts to ${audienceName}` : "Build a clean, campaign-ready contact list in a few steps."}
            onBack={handleBack}
          />
        </div>

        <div className={innerClass}>
          {renderCurrentStep()}
        </div>

        <div className="tw-flex-shrink-0 tw-bg-white tw-mt-8 tw-pt-4 tw-flex tw-items-center tw-justify-between tw-w-full tw-max-w-[985px] tw-mx-auto">
          <Button
            onClick={handleBack}
            className="!tw-flex !tw-h-11 !tw-items-center !tw-gap-2 !tw-rounded-[10px] !tw-border-[#C7C7CC] !tw-border-[0.5px] !tw-bg-white !tw-px-5 !tw-text-[14px] !tw-font-medium !tw-text-[#2E2C27] !tw-shadow-none !tw-font-inter"
          >
            <ArrowLeftOutlined />
            Back
          </Button>

          <Button
            type="primary"
            onClick={handleContinue}
            loading={isProcessing}
            className="!tw-flex !tw-items-center !tw-justify-center !tw-gap-2 !tw-rounded-[8px] !tw-border-0 !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-text-white !tw-font-inter !tw-h-11 !tw-px-6 !tw-text-[14px] !tw-font-semibold !tw-shadow-none"
          >
            {currentStep === maxSteps ? <BookmarkIcon /> : <ArrowRightOutlined />}
            {primaryButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateAudienceFlow;
